import React, { useState, useEffect } from 'react';
import { MapPin, Plus, Trash2, Edit2 } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../services/api';

/**
 * Selector de Paradas Frecuentes
 * Permite seleccionar, crear y gestionar paradas frecuentes
 */
const FrequentStopsSelector = ({ onSelect, onFrequentStopCreated }) => {
    const [stops, setStops] = useState([]);
    const [loading, setLoading] = useState(false);
    const [showCreateForm, setShowCreateForm] = useState(false);

    useEffect(() => {
        loadFrequentStops();
    }, []);

    const loadFrequentStops = async () => {
        try {
            setLoading(true);
            const response = await api.get('/routes/frequent-stops');
            setStops(response.data.stops || []);
        } catch (error) {
            console.error('Error loading frequent stops:', error);
            toast.error('Error al cargar paradas frecuentes');
        } finally {
            setLoading(false);
        }
    };

    const handleSelect = async (stop) => {
        try {
            // Incrementar contador de uso
            await api.post(`/routes/frequent-stops/${stop.id}/use`);

            // Notificar selección
            if (onSelect) {
                onSelect({
                    name: stop.name,
                    address: stop.address,
                    phone: stop.phone,
                    observations: stop.observations,
                    latitude: stop.latitude,
                    longitude: stop.longitude
                });
            }

            toast.success(`Seleccionado: ${stop.name}`);
        } catch (error) {
            console.error('Error al seleccionar parada:', error);
            toast.error('Error al seleccionar parada');
        }
    };

    const handleDelete = async (stop) => {
        if (!window.confirm(`¿Eliminar "${stop.name}"?`)) return;

        try {
            await api.delete(`/routes/frequent-stops/${stop.id}`);
            toast.success('Parada eliminada');
            loadFrequentStops();
        } catch (error) {
            console.error('Error deleting stop:', error);
            toast.error(error.response?.data?.message || 'Error al eliminar');
        }
    };

    if (loading) {
        return (
            <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                <div className="text-center text-gray-500">Cargando paradas...</div>
            </div>
        );
    }

    return (
        <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
                📍 Paradas Frecuentes
            </label>

            {stops.length === 0 ? (
                <div className="p-4 bg-gray-50 rounded-lg border border-gray-200 text-center text-sm text-gray-500">
                    No hay paradas frecuentes guardadas
                </div>
            ) : (
                <div className="space-y-2">
                    {stops.map(stop => (
                        <div
                            key={stop.id}
                            className="p-3 border border-gray-200 rounded-lg hover:bg-purple-50 hover:border-purple-300 cursor-pointer transition-colors group"
                            onClick={() => handleSelect(stop)}
                        >
                            <div className="flex items-start justify-between">
                                <div className="flex-1">
                                    <div className="flex items-center gap-2">
                                        <MapPin className="w-4 h-4 text-purple-600" />
                                        <span className="font-semibold text-gray-900">{stop.name}</span>
                                        {stop.use_count > 0 && (
                                            <span className="text-xs text-gray-500">
                                                (usado {stop.use_count} {stop.use_count === 1 ? 'vez' : 'veces'})
                                            </span>
                                        )}
                                    </div>
                                    <div className="text-sm text-gray-600 mt-1">{stop.address}</div>
                                    {stop.phone && (
                                        <div className="text-xs text-gray-500 mt-1">📞 {stop.phone}</div>
                                    )}
                                    {stop.observations && (
                                        <div className="text-xs text-gray-500 italic mt-1">{stop.observations}</div>
                                    )}
                                </div>
                                <button
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleDelete(stop);
                                    }}
                                    className="opacity-0 group-hover:opacity-100 text-red-500 hover:text-red-700 p-1 transition-opacity"
                                    title="Eliminar"
                                >
                                    <Trash2 className="w-4 h-4" />
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            <div className="mt-2 text-xs text-gray-500">
                💡 Haz clic en una parada para seleccionarla
            </div>
        </div>
    );
};

export default FrequentStopsSelector;
